/**
 * OnboardingTour - First-time user guided tour using React Joyride
 * 
 * Shows callouts with arrows to guide new users through the journal UI
 */

import { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from 'react-joyride';

interface OnboardingTourProps {
    /** Whether to run the tour */
    run: boolean;
    /** Callback when tour completes or is skipped */
    onComplete: () => void;
}

// Tour steps configuration
const tourSteps: Step[] = [
    {
        target: 'body',
        content: (
            <div className="text-center">
                <div className="text-2xl mb-2">👋</div>
                <h3 className="text-lg font-semibold mb-2">Welcome to StudiBudi!</h3>
                <p className="text-slate-600">Let me show you around your learning journal in a few quick steps.</p>
            </div>
        ),
        placement: 'center',
        disableBeacon: true,
    },
    {
        target: '[data-tour="talk-button"]',
        content: (
            <div>
                <h3 className="text-base font-semibold mb-2">🎙️ Talk to StudiBudi</h3>
                <p className="text-sm text-slate-600">
                    Click here to start a conversation with your AI tutor. Just ask a question like "What should I learn about today?"
                </p>
            </div>
        ),
        placement: 'top',
        disableBeacon: true,
    },
    {
        target: '[data-tour="action-cards"]',
        content: (
            <div>
                <h3 className="text-base font-semibold mb-2">📚 Add Learning Content</h3>
                <p className="text-sm text-slate-600">
                    Build your journal with explainers, quizzes, YouTube videos, notes, or PDFs. You can also ask the AI to create content for you!
                </p>
            </div>
        ),
        placement: 'bottom',
        disableBeacon: true,
    },
    {
        target: '[data-tour="todo-list"]',
        content: (
            <div>
                <h3 className="text-base font-semibold mb-2">✅ Track Your Goals</h3>
                <p className="text-sm text-slate-600">
                    Add learning goals and track your progress. StudiBudi can help you create a study plan!
                </p>
            </div>
        ),
        placement: 'auto',
        disableBeacon: true,
    },
    {
        target: '[data-tour="talk-button"]',
        content: (
            <div className="text-center">
                <div className="text-2xl mb-2">🚀</div>
                <h3 className="text-base font-semibold mb-2">You're All Set!</h3>
                <p className="text-sm text-slate-600">
                    Now click "Talk to StudiBudi" to get started. Ask anything and start learning!
                </p>
            </div>
        ),
        placement: 'top',
        disableBeacon: true,
    },
];

export const OnboardingTour = ({ run, onComplete }: OnboardingTourProps) => {
    const [stepIndex, setStepIndex] = useState(0);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { action, index, status, type } = data;

        // Handle tour completion or skip
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            onComplete();
            return;
        }

        // Handle step navigation
        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
        }
    };

    // Reset step index when tour restarts
    useEffect(() => {
        if (run) {
            setStepIndex(0);
        }
    }, [run]);

    return (
        <Joyride
            run={run}
            steps={tourSteps}
            stepIndex={stepIndex}
            continuous
            showProgress
            showSkipButton
            scrollToFirstStep
            spotlightClicks
            disableScrolling
            callback={handleJoyrideCallback}
            styles={{
                options: {
                    primaryColor: '#1e293b', // slate-800
                    backgroundColor: '#ffffff',
                    textColor: '#334155', // slate-700
                    arrowColor: '#ffffff',
                    overlayColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 10000,
                },
                buttonNext: {
                    backgroundColor: '#1e293b',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                },
                buttonBack: {
                    color: '#64748b',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                },
                buttonSkip: {
                    color: '#94a3b8',
                    fontSize: '13px',
                },
                tooltip: {
                    borderRadius: '12px',
                    padding: '20px',
                },
                tooltipContent: {
                    padding: '0',
                },
                spotlight: {
                    borderRadius: '12px',
                },
            }}
            locale={{
                back: 'Back',
                close: 'Close',
                last: 'Get Started!',
                next: 'Next',
                skip: 'Skip tour',
            }}
        />
    );
};

export default OnboardingTour;
